import { useState } from 'react';
import { useLang } from '../hooks/useLang';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { t } from '../translations';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kober-energy/artifacts/vys7c9cr_3.png";

// Animation variants — module-scope to avoid new object refs on every render
const ANIM_404    = { initial: { opacity: 0, y: -20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7 } };
const ANIM_LOGO   = { initial: { opacity: 0, scale: 0.9 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.6, delay: 0.15 } };
const ANIM_MSG    = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: 0.3 } };
const ANIM_CTA    = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.45 } };

export default function NotFoundPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useLang();
  const [showContact, setShowContact] = useState(false);
  const text = t[lang];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <Navbar text={text.nav} lang={lang} setLang={setLang} onContact={() => setShowContact(true)} />

      {/* Grid overlay */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(212,160,23,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212,160,23,0.03) 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
        }}
      />

      <main className="flex-1 flex items-center justify-center px-6 relative z-10">
        <div className="text-center max-w-2xl mx-auto">

          {/* 404 number */}
          <motion.div {...ANIM_404} className="relative mb-8 select-none">
            <span
              className="text-[clamp(120px,20vw,200px)] font-bold leading-none"
              style={{
                fontFamily: "'Outfit', sans-serif",
                background: 'linear-gradient(135deg, rgba(212,160,23,0.12) 0%, rgba(212,160,23,0.04) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.04em',
              }}
            >
              404
            </span>
            {/* Gold divider line */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-24 h-px bg-[#D4A017]/40" />
          </motion.div>

          {/* Logo */}
          <motion.img
            {...ANIM_LOGO}
            src={LOGO_URL}
            alt="KÖBER Energy Holding"
            className="h-10 w-auto mx-auto mb-8 opacity-70"
          />

          {/* Message */}
          <motion.div {...ANIM_MSG}>
            <h1
              className="text-3xl sm:text-4xl font-semibold text-white mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {lang === 'RO' ? 'Pagină negăsită' : 'Page not found'}
            </h1>
            <p className="text-white/45 text-base leading-relaxed mb-10 max-w-md mx-auto">
              {lang === 'RO'
                ? 'Această pagină nu există sau a fost mutată. Reveniți la pagina principală pentru a continua.'
                : "This page doesn't exist or has been moved. Head back to the homepage to continue."}
            </p>
          </motion.div>

          {/* CTA */}
          <motion.button
            {...ANIM_CTA}
            onClick={() => navigate('/')}
            data-testid="not-found-home-btn"
            className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#E5B020] text-black font-semibold px-8 py-3.5 transition-colors duration-200"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            <ArrowLeft size={15} />
            {lang === 'RO' ? 'Înapoi acasă' : 'Back to Home'}
          </motion.button>
        </div>
      </main>

      <Footer text={text.footer} lang={lang} onContact={() => setShowContact(true)} />

      <ContactModal
        open={showContact}
        onClose={() => setShowContact(false)}
        text={text.contact}
        lang={lang}
      />
    </div>
  );
}
